import React, { useMemo } from 'react';
import { Accordion, Button, Col, Form, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import ValidationSummary from './ValidationSummary';
import type { ScheduleUI } from '../types';

interface Action {
  icon: any;
  title: string;
  onClick: () => void;
  variant?: 'outline-secondary' | 'danger';
}

interface Props {
  schedule: ScheduleUI;
  onChange: (patch: Partial<ScheduleUI>) => void;
  actions: Action[];
  zones: Array<{ zone_id: number; name: string }>;
  schedulingWindows: Array<{
    scheduling_window_id: number;
    name: string;
    zone_name?: string;
  }>;
  forbiddenScheduleDependencyWindowIds?: Set<number>;
}

const range = (start: number, end: number) => Array.from({ length: end - start + 1 }, (_, i) => start + i);

const monthName = (m: number) => new Date(2000, m - 1, 1).toLocaleString(undefined, { month: 'long' });

const handleSelectNumber = (value: string, min: number, max: number): number | undefined => {
  if (value === '') return undefined;
  const n = Number(value);
  if (!Number.isFinite(n) || n < min || n > max) return undefined;
  return n;
};

const getDatePart = (val?: string): string => {
  if (!val) return '';
  return val.split('T')[0];
};

const getTimePart = (val?: string): string => {
  if (!val || !val.includes('T')) return '';
  const time = val.split('T')[1].substring(0, 5);
  if (time === '00:00') return '';
  return time;
};

const combineDatetime = (datePart: string, timePart: string): string => {
  if (!timePart) return datePart;
  return `${datePart}T${timePart}`;
};

const MonthSelect: React.FC<{
  label: string;
  value?: number;
  onChange: (n?: number) => void;
  placeholder?: string;
}> = ({ label, value, onChange, placeholder = '--' }) => (
  <Form.Group>
    <Form.Label>{label}</Form.Label>
    <Form.Select value={value ?? ''} onChange={(e) => onChange(handleSelectNumber(e.target.value, 1, 12))}>
      <option value="">{placeholder}</option>
      {range(1, 12).map((m) => (
        <option key={m} value={m}>
          {monthName(m)}
        </option>
      ))}
    </Form.Select>
  </Form.Group>
);

const DomSelect: React.FC<{
  label: string;
  value?: number;
  onChange: (n?: number) => void;
  placeholder?: string;
}> = ({ label, value, onChange, placeholder = '--' }) => (
  <Form.Group>
    <Form.Label>{label}</Form.Label>
    <Form.Select value={value ?? ''} onChange={(e) => onChange(handleSelectNumber(e.target.value, 1, 31))}>
      <option value="">{placeholder}</option>
      {range(1, 31).map((d) => (
        <option key={d} value={d}>
          {d}
        </option>
      ))}
    </Form.Select>
  </Form.Group>
);

const WomSelect: React.FC<{
  label: string;
  value?: number;
  onChange: (n?: number) => void;
  placeholder?: string;
}> = ({ label, value, onChange, placeholder = '--' }) => (
  <Form.Group>
    <Form.Label>{label}</Form.Label>
    <Form.Select value={value ?? ''} onChange={(e) => onChange(handleSelectNumber(e.target.value, 1, 5))}>
      <option value="">{placeholder}</option>
      <option value="1">First</option>
      <option value="2">Second</option>
      <option value="3">Third</option>
      <option value="4">Fourth</option>
      <option value="5">Last</option>
    </Form.Select>
  </Form.Group>
);

const DowSelect: React.FC<{
  label: string;
  value?: number;
  onChange: (n?: number) => void;
  placeholder?: string;
}> = ({ label, value, onChange, placeholder = '--' }) => (
  <Form.Group>
    <Form.Label>{label}</Form.Label>
    <Form.Select value={value ?? ''} onChange={(e) => onChange(handleSelectNumber(e.target.value, 1, 7))}>
      <option value="">{placeholder}</option>
      <option value="1">Mon</option>
      <option value="2">Tue</option>
      <option value="3">Wed</option>
      <option value="4">Thu</option>
      <option value="5">Fri</option>
      <option value="6">Sat</option>
      <option value="7">Sun</option>
    </Form.Select>
  </Form.Group>
);

const ordinalWords = [
  'First',
  'Second',
  'Third',
  'Fourth',
  'Fifth',
  'Sixth',
  'Seventh',
  'Eighth',
  'Ninth',
  'Tenth',
  'Eleventh',
  'Twelfth',
  'Thirteenth',
  'Fourteenth',
  'Fifteenth',
  'Sixteenth',
  'Seventeenth',
  'Eighteenth',
  'Nineteenth',
  'Twentieth',
  'Twenty-first',
  'Twenty-second',
  'Twenty-third',
  'Twenty-fourth',
  'Twenty-fifth',
  'Twenty-sixth',
  'Twenty-seventh',
  'Twenty-eighth',
  'Twenty-ninth',
  'Thirtieth',
  'Thirty-first',
];

const ordinalSuffix = (n: number) => {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return 'th';
  switch (n % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

const fromLastLabel = (n: number) => `${n}${ordinalSuffix(n)} from last`;

const NthSelect: React.FC<{
  label: string;
  value?: number;
  onChange: (n?: number) => void;
  placeholder?: string;
}> = ({ label, value, onChange, placeholder = '--' }) => (
  <Form.Group>
    <Form.Label>{label}</Form.Label>
    <Form.Select value={value ?? ''} onChange={(e) => onChange(handleSelectNumber(e.target.value, -30, 31))}>
      <option value="">{placeholder}</option>

      <optgroup label="From start">
        {range(1, 31).map((d) => (
          <option key={d} value={d}>
            {ordinalWords[d - 1]}
          </option>
        ))}
      </optgroup>

      <optgroup label="From end">
        <option value={0}>Last</option>
        {Array.from({ length: 30 }, (_, i) => -(i + 1)).map((n) => {
          const k = Math.abs(n);
          return (
            <option key={n} value={n}>
              {`${fromLastLabel(k)}`}
            </option>
          );
        })}
      </optgroup>
    </Form.Select>

    <Form.Text className="text-muted">
      1 till 31 = the Nth day of month in schedule
      <br />0 = last day of month in schedule
    </Form.Text>
  </Form.Group>
);

const WindowSelect: React.FC<{
  label: string;
  value?: string;
  onChange: (v?: string) => void;
  groups: {
    zoneName: string;
    windows: Array<{
      name: string;
      zone_name?: string;
      scheduling_window_id: number;
    }>;
  }[];
  placeholder?: string;
}> = ({ label, value, onChange, groups, placeholder = '--' }) => (
  <Form.Group>
    <Form.Label>{label}</Form.Label>
    <Form.Select value={value ?? ''} onChange={(e) => onChange(e.target.value || undefined)}>
      <option value="">{placeholder}</option>
      {groups.map(({ zoneName, windows }) =>
        windows.length === 0 ? null : (
          <optgroup key={zoneName} label={zoneName}>
            {windows.map((w) => (
              <option key={w.scheduling_window_id} value={`${w.zone_name || zoneName}|${w.name}`}>
                {w.name}
              </option>
            ))}
          </optgroup>
        )
      )}
    </Form.Select>
  </Form.Group>
);

const ScheduleCard: React.FC<Props> = ({ schedule, onChange, actions, zones, schedulingWindows, forbiddenScheduleDependencyWindowIds }) => {
  const { t } = useTranslation();

  const errors = useMemo(() => {
    const errs: string[] = [];
    const exactlyOnePicked = <T,>(a?: T, b?: T) => (a != null && b == null) || (a == null && b != null);

    const normalizeTime = (time?: string) => {
      if (!time) return undefined;
      if (time.split(':').length === 3) return time;
      if (time.split(':').length === 2) return `${time}:00`;
      return time;
    };

    if (schedule.start_time && schedule.end_time && normalizeTime(schedule.start_time) === normalizeTime(schedule.end_time)) {
      errs.push('Start Time and End Time cannot be the same.');
    }
    if (exactlyOnePicked(schedule.date_from, schedule.date_until)) {
      errs.push('Both Date From and Date Until must be selected (or neither).');
    }
    if (schedule.date_from && schedule.date_until) {
      const fromDate = getDatePart(schedule.date_from);
      const untilDate = getDatePart(schedule.date_until);
      if (fromDate > untilDate) {
        errs.push('Date From must be before or equal to Date Until.');
      }
    }
    if (exactlyOnePicked(schedule.month_from, schedule.month_to)) {
      errs.push('Both Month From and Month Until must be selected (or neither).');
    }
    if (schedule.month_from && schedule.month_to && schedule.month_from > schedule.month_to) {
      errs.push('Month From must be ≤ Month Until.');
    }

    if (exactlyOnePicked(schedule.wom_from, schedule.wom_to)) {
      errs.push('Both Week of Month From and Week of Month Until must be selected (or neither).');
    }
    if (schedule.wom_from && schedule.wom_to && schedule.wom_from > schedule.wom_to) {
      errs.push('Week of Month From must be ≤ Week of Month Until.');
    }
    if (exactlyOnePicked(schedule.dow_from, schedule.dow_to)) {
      errs.push('Both Day of Week From and Day of Week Until must be selected (or neither).');
    }
    if (schedule.dow_from && schedule.dow_to && schedule.dow_from > schedule.dow_to) {
      errs.push('Day of Week From must be ≤ Day of Week Until.');
    }
    if (exactlyOnePicked(schedule.nth_of_month_from, schedule.nth_of_month_until)) {
      errs.push('Both Nth From and Nth Until must be selected (or neither).');
    }

    if (schedule.nth_of_month_from != null && schedule.nth_of_month_until != null) {
      const from = schedule.nth_of_month_from;
      const until = schedule.nth_of_month_until;

      const fromIsStartBased = from > 0;
      const untilIsStartBased = until > 0;

      if (fromIsStartBased && untilIsStartBased && from > until) {
        errs.push('Nth From must be ≤ Nth Until.');
      }

      if (!fromIsStartBased && !untilIsStartBased && from > until) {
        errs.push('Nth From must be ≤ Nth Until.');
      }

      if (!fromIsStartBased && untilIsStartBased) {
        errs.push('Nth From and Nth Until range is invalid (cannot start from end and end at beginning).');
      }
    }

    return errs;
  }, [schedule]);

  const buildWindowOptionGroups = (
    windows: typeof schedulingWindows
  ): {
    zoneName: string;
    windows: typeof schedulingWindows;
  }[] => {
    const byZone: Record<string, typeof schedulingWindows> = {};
    for (const w of windows) {
      const key = w.zone_name || 'Unassigned';
      if (!byZone[key]) byZone[key] = [];
      byZone[key].push(w);
    }
    const explicit = zones.map((z) => z.name);
    const leftovers = Object.keys(byZone).filter((zn) => !explicit.includes(zn));
    const ordered = [...explicit, ...leftovers];
    const groups = ordered.map((zoneName) => ({
      zoneName,
      windows: byZone[zoneName] || [],
    }));
    return groups;
  };

  const dependencyWindowOptionGroups = useMemo(
    () => buildWindowOptionGroups(schedulingWindows.filter((w) => !forbiddenScheduleDependencyWindowIds?.has(w.scheduling_window_id))),
    [schedulingWindows, zones, forbiddenScheduleDependencyWindowIds]
  );

  const set = (patch: Partial<ScheduleUI>) => onChange(patch);

  return (
    <div className="border p-3 mb-3 rounded">
      <Row className="mb-2">
        <Col>
          <Form.Label>{t('Schedule Name')}</Form.Label>
          <Form.Control
            placeholder={t('Schedule Name') as string}
            value={schedule.scheduleName ?? ''}
            onChange={(e) => set({ scheduleName: e.target.value })}
          />
        </Col>
        <Col xs="auto" className="d-flex align-items-end gap-2">
          {actions.map((a, i) => (
            <Button key={i} size="sm" variant={a.variant || 'outline-secondary'} onClick={a.onClick} title={a.title}>
              <FontAwesomeIcon icon={a.icon} />
            </Button>
          ))}
        </Col>
      </Row>

      <Row className="mb-2">
        <Col>
          <Form.Group>
            <Form.Label>{t('Description')}</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder={t('Description') as string}
              value={schedule.description ?? ''}
              onChange={(e) => set({ description: e.target.value || undefined })}
            />
            <Form.Text className="text-muted">{t('Optional. Explains what this schedule means to end-users.')}</Form.Text>
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md>
          <Form.Group>
            <Form.Label>{t('Schedule Timezone')}</Form.Label>
            <Form.Select value={schedule.timezone || ''} onChange={(e) => set({ timezone: e.target.value || undefined })}>
              <option value="">{t('Use window timezone')}</option>

              {Intl.supportedValuesOf('timeZone').map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <h6 className="text-muted fw-semibold mt-3 mb-2">{t('Time')}</h6>
      <Row className="mb-2">
        <Col md>
          <Form.Group>
            <Form.Label>{t('Start Time')}</Form.Label>
            <Form.Control type="time" step="1" value={schedule.start_time || ''} onChange={(e) => set({ start_time: e.target.value || undefined })} />
            <Form.Text className="text-muted">{t('Format: HH:MM:SS or HH:MM')}</Form.Text>
          </Form.Group>
        </Col>
        <Col md>
          <Form.Group>
            <Form.Label>{t('End Time')}</Form.Label>
            <Form.Control type="time" step="1" value={schedule.end_time || ''} onChange={(e) => set({ end_time: e.target.value || undefined })} />
            <Form.Text className="text-muted">{t('Format: HH:MM:SS or HH:MM')}</Form.Text>
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-2">
        <Col md>
          <Form.Group>
            <Form.Label>{t('Date From')}</Form.Label>
            <Form.Control
              type="date"
              value={getDatePart(schedule.date_from)}
              onChange={(e) => {
                const datePart = e.target.value;
                if (!datePart) {
                  set({ date_from: undefined });
                  return;
                }
                const timePart = getTimePart(schedule.date_from);
                set({ date_from: combineDatetime(datePart, timePart) });
              }}
            />
          </Form.Group>
          {getDatePart(schedule.date_from) && (
            <Form.Group className="mt-1">
              <Form.Check
                type="checkbox"
                label={t('Set start time on first day')}
                checked={!!getTimePart(schedule.date_from)}
                onChange={(e) => {
                  const datePart = getDatePart(schedule.date_from);
                  if (!datePart) return;
                  set({
                    date_from: e.target.checked ? combineDatetime(datePart, '08:00') : datePart,
                  });
                }}
              />
              {!!getTimePart(schedule.date_from) && (
                <Form.Control
                  type="time"
                  className="mt-1"
                  value={getTimePart(schedule.date_from)}
                  onChange={(e) => {
                    const datePart = getDatePart(schedule.date_from);
                    if (!datePart) return;
                    set({
                      date_from: combineDatetime(datePart, e.target.value),
                    });
                  }}
                />
              )}
              <Form.Text className="text-muted">{t('Overrides Start Time on the first day.')}</Form.Text>
            </Form.Group>
          )}
        </Col>
        <Col md>
          <Form.Group>
            <Form.Label>{t('Date Until')}</Form.Label>
            <Form.Control
              type="date"
              value={getDatePart(schedule.date_until)}
              onChange={(e) => {
                const datePart = e.target.value;
                if (!datePart) {
                  set({ date_until: undefined });
                  return;
                }
                const timePart = getTimePart(schedule.date_until);
                set({ date_until: combineDatetime(datePart, timePart) });
              }}
            />
          </Form.Group>
          {getDatePart(schedule.date_until) && (
            <Form.Group className="mt-1">
              <Form.Check
                type="checkbox"
                label={t('Set end time on last day')}
                checked={!!getTimePart(schedule.date_until)}
                onChange={(e) => {
                  const datePart = getDatePart(schedule.date_until);
                  if (!datePart) return;
                  set({
                    date_until: e.target.checked ? combineDatetime(datePart, '16:00') : datePart,
                  });
                }}
              />
              {!!getTimePart(schedule.date_until) && (
                <Form.Control
                  type="time"
                  className="mt-1"
                  value={getTimePart(schedule.date_until)}
                  onChange={(e) => {
                    const datePart = getDatePart(schedule.date_until);
                    if (!datePart) return;
                    set({
                      date_until: combineDatetime(datePart, e.target.value),
                    });
                  }}
                />
              )}
              <Form.Text className="text-muted">{t('Overrides End Time on the last day.')}</Form.Text>
            </Form.Group>
          )}
        </Col>
      </Row>

      <Row className="mb-2">
        <Col md>
          <MonthSelect label={t('Month From') as string} value={schedule.month_from} onChange={(v) => set({ month_from: v })} />
        </Col>
        <Col md>
          <MonthSelect label={t('Month Until') as string} value={schedule.month_to} onChange={(v) => set({ month_to: v })} />
        </Col>
      </Row>

      <Row className="mb-2">
        <Col md>
          <WomSelect label={t('Week of Month From') as string} value={schedule.wom_from} onChange={(v) => set({ wom_from: v })} />
        </Col>
        <Col md>
          <WomSelect label={t('Week of Month Until') as string} value={schedule.wom_to} onChange={(v) => set({ wom_to: v })} />
        </Col>
      </Row>

      <Row className="mb-2">
        <Col md>
          <DowSelect label={t('Day of Week From') as string} value={schedule.dow_from} onChange={(v) => set({ dow_from: v })} />
        </Col>
        <Col md>
          <DowSelect label={t('Day of Week Until') as string} value={schedule.dow_to} onChange={(v) => set({ dow_to: v })} />
        </Col>
      </Row>

      <Accordion className="mt-3" alwaysOpen>
        <Accordion.Item eventKey="nth">
          <Accordion.Header>{t('Advanced: Nth-of-month')}</Accordion.Header>
          <Accordion.Body>
            <Row className="mb-2">
              <Col md>
                <NthSelect label={t('Nth From') as string} value={schedule.nth_of_month_from} onChange={(v) => set({ nth_of_month_from: v })} />
              </Col>
              <Col md>
                <NthSelect label={t('Nth Until') as string} value={schedule.nth_of_month_until} onChange={(v) => set({ nth_of_month_until: v })} />
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md>
                <Form.Group>
                  <Form.Label>{t('Shift during window')}</Form.Label>
                  <Form.Select
                    value={schedule.shift_if_closed_window_path ?? ''}
                    onChange={(e) =>
                      set({
                        shift_if_closed_window_path: e.target.value === '' ? undefined : e.target.value,
                      })
                    }
                  >
                    <option value="">{t('Select Window')}</option>
                    {dependencyWindowOptionGroups.map(({ zoneName, windows }) =>
                      windows.length === 0 ? null : (
                        <optgroup key={zoneName} label={zoneName}>
                          {windows.map((w) => (
                            <option key={w.scheduling_window_id} value={`${w.zone_name || zoneName}|${w.name}`}>
                              {w.name}
                            </option>
                          ))}
                        </optgroup>
                      )
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md>
                <Form.Group>
                  <Form.Label>{t('Shift Open Days')}</Form.Label>
                  <Form.Control
                    type="number"
                    step="1"
                    placeholder="e.g. -2, 0, 3"
                    value={schedule.shift_open_days ?? ''}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === '') return set({ shift_open_days: undefined });
                      const n = Number(raw);
                      set({
                        shift_open_days: Number.isFinite(n) ? n : undefined,
                      });
                    }}
                  />
                  <Form.Text className="text-muted">{t('Negative = backward, positive = forward')}</Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-2">
              <Col md>
                <WindowSelect
                  label={t('Nth From: In Scheduling Window') as string}
                  value={schedule.nth_of_month_from_in_scheduling_window_path}
                  onChange={(v) => set({ nth_of_month_from_in_scheduling_window_path: v })}
                  groups={dependencyWindowOptionGroups}
                  placeholder={t('Select Window') as string}
                />
              </Col>
              <Col md>
                <WindowSelect
                  label={t('Nth Until: In Scheduling Window') as string}
                  value={schedule.nth_of_month_until_in_scheduling_window_path}
                  onChange={(v) => set({ nth_of_month_until_in_scheduling_window_path: v })}
                  groups={dependencyWindowOptionGroups}
                  placeholder={t('Select Window') as string}
                />
              </Col>
            </Row>

            <Row className="mb-2">
              <Col md>
                <WindowSelect
                  label={t('Open During: Scheduling Window') as string}
                  value={schedule.open_during_scheduling_window_path}
                  onChange={(v) => set({ open_during_scheduling_window_path: v })}
                  groups={dependencyWindowOptionGroups}
                  placeholder={t('Select Window') as string}
                />
              </Col>
              <Col md>
                <WindowSelect
                  label={t('Closed During: Scheduling Window') as string}
                  value={schedule.closed_during_scheduling_window_path}
                  onChange={(v) => set({ closed_during_scheduling_window_path: v })}
                  groups={dependencyWindowOptionGroups}
                  placeholder={t('Select Window') as string}
                />
              </Col>
            </Row>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <ValidationSummary messages={errors} />
    </div>
  );
};

export default ScheduleCard;
